import React, { useState, useEffect } from 'react'
import { Button, message } from 'antd'
import { EditOutlined } from '@ant-design/icons'
import AdherenceRing from './AdherenceRing'
import HeroStat from './HeroStat'
import PillarColumn from './PillarColumn'
import ProtocolInfographicEditor from './ProtocolInfographicEditor'
import { updateProtocolInfographic } from '../../../../api/assessmentReview'
import styles from './ProtocolInfographic.module.css'

export { PRIORITY_STYLES, PILLARS } from './constants'
export { default as PriorityBadge } from './PriorityBadge'
export { default as AdherenceRing } from './AdherenceRing'
export { default as AdherenceRingLight } from './AdherenceRingLight'
export { default as HeroStat } from './HeroStat'
export { default as PillarColumn } from './PillarColumn'
export { default as RecCard } from './RecCard'
export { default as ProtocolInfographicEditor } from './ProtocolInfographicEditor'

export function ProtocolInfographic({
  clientName = 'Client',
  sessionDate,
  data,
  pillar,
  clientId,
  assessmentId,
  editabled = false,
  onUpdate
}) {
  const [editing, setEditing] = useState(false)
  const [editData, setEditData] = useState(data || {})
  const [displayData, setDisplayData] = useState(data || {})
  const [saving, setSaving] = useState(false)

  // Update displayData when props.data changes
  useEffect(() => {
    setDisplayData(data)
  }, [data])

  if (!data || !pillar) {
    return null
  }

  const handleEdit = () => {
    setEditData(displayData)
    setEditing(true)
  }

  const handleSave = async () => {
    if (!clientId || !assessmentId) {
      message.error('Missing client or assessment ID')
      return
    }

    setSaving(true)
    try {
      await updateProtocolInfographic(clientId, assessmentId, pillar.key, editData)
      setDisplayData(editData) // Update local display data immediately
      if (onUpdate) {
        onUpdate(editData)
      }
      message.success('Infographic updated successfully!')
      setEditing(false)
    } catch (error) {
      message.error('Failed to save changes. Please try again.')
      console.error('Error saving infographic:', error)
    } finally {
      setSaving(false)
    }
  }

  const handleCancel = () => {
    setEditData(displayData)
    setEditing(false)
  }

  const overallAdherence = (displayData.key_metrics && displayData.key_metrics.current_adherence) || 0
  const totalCritical = (displayData.recommendations && displayData.recommendations.filter((r) => r.priority === 'critical')).length || 0
  const totalRecs = (displayData.summary && displayData.summary.total_recommendations) || 0

  if (editing) {
    return (
      <ProtocolInfographicEditor
        data={editData}
        pillar={pillar}
        onChange={setEditData}
        onSave={handleSave}
        onCancel={handleCancel}
        saving={saving}
        clientId={clientId}
        assessmentId={assessmentId}
      />
    )
  }

  return (
    <div className={styles.container}>
      <div className={styles.heroHeader}>
        <div className={styles.headerLeft}>
          <h1 className={styles.headerTitle}>{pillar.label} Recommendations</h1>
        </div>

        <div className={styles.headerRight}>
          <HeroStat label="Adherence" value={`${overallAdherence}%`} accent={pillar.accent} />
          <div className={styles.divider} />
          <HeroStat label="Critical" value={String(totalCritical)} accent="#f5222d" />
          <div className={styles.divider} />
          <HeroStat label="Total Recs" value={String(totalRecs)} accent="#111827" />
          <AdherenceRing value={overallAdherence} accent={pillar.accent} />
        </div>
      </div>

      {editabled && (
        <div style={{ textAlign: 'right', marginBottom: '12px' }}>
          <Button
            type="text"
            onClick={handleEdit}
            icon={<EditOutlined />}
            style={{ color: '#264382' }}
          >
            Refine with AI
          </Button>
        </div>
      )}

      <div className={styles.pillarGrid}>
        <PillarColumn pillar={pillar} data={displayData} />
      </div>
    </div>
  )
}
